import { type ThemeOptions } from "@mui/material";
import { colord, extend } from "colord";
import mixPlugin from "colord/plugins/mix";

import { createMaterialThemeOptions } from "../createMaterialThemeOptions";
import type { DesignerTheme } from "../DesignerTheme";
import { colors } from "../Palette";
import { lightScheme } from "./colorScheme";
import { themeCore } from "./themeCore";

extend([mixPlugin]);

const { interactBlue, uiGrey } = colors;

const lightThemeOptions: Omit<DesignerTheme<"light">, "materialThemeOptions"> = {
  name: "light" as const,
  type: "light",
  ...themeCore,

  global: `
    :root {
      color-scheme: light;
    }
    .icon-color-1 {
      fill: #76CBEE;
    }
    .icon-color-2 {
      fill: #C9E9F4;
    }
    .icon-color-3 {
      fill: #ACDEF3;
    }
    .icon-color-4 {
      fill: #fff;
    }
  `,

  boxShadow: {
    normal: `0px 2px 6px -2px ${colord(uiGrey["900"]).mix(interactBlue["400"], 0.4).alpha(0.25).toRgbString()}`,
    hover: `0px 6px 8px ${colord(uiGrey["900"]).mix(interactBlue["400"], 0.4).alpha(0.25).toRgbString()}`,
    selected: `0px 5px 10px ${colord(uiGrey["900"]).mix(interactBlue["400"], 0.4).alpha(0.25).toRgbString()}`,
    interacting: `0px 8px 16px ${colord(uiGrey["900"]).mix(interactBlue["400"], 0.4).alpha(0.25).toRgbString()}`,
    below: `0px 10px 10px -4px ${colord(uiGrey["900"]).mix(interactBlue["400"], 0.4).alpha(0.25).toRgbString()}`,
  },

  moduleMap: {
    background: lightScheme.surfaceBack_2,
    backgroundSecondary: "#C9CED6",
    connectorColor: colord(lightScheme.outlineMedium).mix(lightScheme.primary, 0.25).toHex(),
  },

  colorGroup: [
    "#ffa726",
    "#ab47bc",
    "#66bb6a",
    "#5c6bc0",
    "#ef5350",
    "#26a69a",
    "#29b6f6",
    "#ffca28",
    "#ec407a",
    "#9ccc65",
    "#7e57c2",
    "#26c6da",
    "#d4e157",
    "#42a5f5",
  ],
  ...lightScheme,
};

const lightMaterialThemeOptions = (theme: Omit<DesignerTheme<"light">, "materialThemeOptions">): ThemeOptions => {
  const materialThemeOptions = createMaterialThemeOptions(theme);
  return {
    ...materialThemeOptions,
    palette: {
      ...materialThemeOptions.palette,
      mode: "light",
      background: {
        paper: "#fff",
        default: "#fff",
      },
      text: {
        primary: theme.generalTextIcon,
        secondary: "rgba(0, 0, 0, 0.65)",
        disabled: "rgba(0, 0, 0, 0.45)",
      },
      grey: uiGrey,
    },
  };
};

export const LightTheme: DesignerTheme<"light"> = {
  ...lightThemeOptions,
  materialThemeOptions: lightMaterialThemeOptions(lightThemeOptions),
} as const;
